"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.default = buildMentalRotationTimeline;
require("regenerator-runtime/runtime");
// setup
const taskSetup_1 = require("../taskSetup");
const helpers_1 = require("../shared/helpers");
// trials
const instructions_1 = require("./trials/instructions");
const trials_1 = require("../shared/trials");
const config_1 = require("./helpers/config");
const taskStore_1 = require("../../taskStore");
const batchPreloading_1 = require("../shared/helpers/batchPreloading");
const downexInstructions_1 = require("./trials/downexInstructions");
function buildMentalRotationTimeline(config, mediaAssets) {
    const { runCat, heavyInstructions } = (0, taskStore_1.taskStore)();
    const { semThreshold } = (0, taskStore_1.taskStore)();
    let playedThreeDimInstructions = false;
    let playedPolygonInstructions = false;
    (0, helpers_1.initTrialSaving)(config);
    const initialTimeline = (0, helpers_1.initTimeline)(config, trials_1.enterFullscreen);
    const ifRealTrialResponse = {
        timeline: [(0, trials_1.getAudioResponse)(mediaAssets)],
        conditional_function: () => {
            const stim = (0, taskStore_1.taskStore)().nextStimulus;
            if (runCat) {
                // this trial is never used after a practice trial when running in cat
                return true;
            }
            if (stim.assessmentStage === 'practice_response') {
                return false;
            }
            return true;
        },
    };
    let corpus = (0, taskStore_1.taskStore)().corpora.stimulus;
    const translations = (0, taskStore_1.taskStore)().translations;
    const validationErrorMap = {};
    const layoutConfigMap = {};
    for (const c of corpus) {
        const { itemConfig, errorMessages } = (0, config_1.getLayoutConfig)(c, translations, mediaAssets);
        layoutConfigMap[c.itemId] = itemConfig;
        if (errorMessages.length) {
            validationErrorMap[c.itemId] = errorMessages.join('; ');
        }
    }
    if (Object.keys(validationErrorMap).length) {
        console.error('The following errors were found');
        console.table(validationErrorMap);
        throw new Error('Something went wrong. Please look in the console for error details');
    }
    // organize media assets into batches for preloading
    const batchSize = 25;
    const batchedCorpus = (0, helpers_1.batchTrials)(corpus, batchSize);
    const batchedMediaAssets = (0, helpers_1.batchMediaAssets)(mediaAssets, batchedCorpus, ['item', 'answer', 'distractors']);
    // counter for next batch to preload (skipping the initial preload)
    let currPreloadBatch = 0;
    const initialMedia = (0, batchPreloading_1.getLeftoverAssets)(batchedMediaAssets, mediaAssets);
    const initialPreload = (0, helpers_1.createPreloadTrials)(runCat ? mediaAssets : initialMedia).default;
    const instructions = heavyInstructions
        ? downexInstructions_1.downexInstructions
        : [instructions_1.imageInstructions, instructions_1.videoInstructionsMisfit, instructions_1.videoInstructionsFit];
    const timeline = [initialPreload, initialTimeline, ...instructions];
    const trialConfig = {
        trialType: 'audio',
        responseAllowed: true,
        promptAboveButtons: true,
        task: config.task,
        layoutConfig: {
            showPrompt: true,
        },
        layoutConfigMap,
        terminateCat: runCat, // if running cat, stop if 4 of last 10 trials have been incorrect
    };
    // runs with adaptive algorithm if cat enabled
    const stimulusBlock = {
        timeline: [(0, trials_1.afcStimulusTemplate)(trialConfig), ifRealTrialResponse],
        // true = execute normally, false = skip
        conditional_function: () => {
            if ((0, taskStore_1.taskStore)().skipCurrentTrial) {
                (0, taskStore_1.taskStore)('skipCurrentTrial', false);
                return false;
            }
            if (runCat && taskSetup_1.cat._seMeasurement < semThreshold) {
                return false;
            }
            return true;
        },
    };
    const firstBlockPractice = corpus.filter((trial) => Number(trial.block_index) === 1 && trial.assessmentStage === 'practice_response');
    let fellBack = false;
    const fallbackInstructions = {
        timeline: [
            trials_1.repeatInstructionsMessage,
            ...downexInstructions_1.downexInstructions,
            ...firstBlockPractice.map((trial) => (0, trials_1.afcStimulusTemplate)(trialConfig, trial)),
        ],
        conditional_function: () => {
            const run = (0, helpers_1.checkFallbackCriteria)() && !fellBack;
            if (run) {
                fellBack = true;
            }
            return run;
        },
    };
    // put polygon and 3D practice in their own blocks so they run at the right time
    const polygonPractice = corpus.filter((trial) => trial.trialType === 'polygon' && trial.assessmentStage === 'practice_response');
    const threeDimPractice = corpus.filter((trial) => trial.trialType === '3D' && trial.assessmentStage === 'practice_response');
    corpus = corpus.filter((trial) => !polygonPractice.includes(trial) && !threeDimPractice.includes(trial));
    (0, taskStore_1.taskStore)('corpora', {
        downex: (0, taskStore_1.taskStore)().corpora.downex,
        stimulus: corpus,
    });
    const threeDimInstructBlock = {
        timeline: [
            instructions_1.threeDimInstructions,
            ...threeDimPractice.map((trial) => (0, trials_1.afcStimulusTemplate)(trialConfig, trial)),
            { ...trials_1.fixationOnly, stimulus: '' },
        ],
        conditional_function: () => {
            if ((0, taskStore_1.taskStore)().nextStimulus.trialType === '3D' && !playedThreeDimInstructions) {
                playedThreeDimInstructions = true;
                return true;
            }
            return false;
        },
    };
    const polygonInstructBlock = {
        timeline: [
            instructions_1.polygonInstructions,
            ...polygonPractice.map((trial) => (0, trials_1.afcStimulusTemplate)(trialConfig, trial)),
            { ...trials_1.fixationOnly, stimulus: '' },
        ],
        conditional_function: () => {
            if ((0, taskStore_1.taskStore)().nextStimulus.trialType === 'polygon' && !playedPolygonInstructions && heavyInstructions) {
                playedPolygonInstructions = true;
                return true;
            }
            return false;
        },
    };
    function preloadBatch() {
        timeline.push((0, helpers_1.createPreloadTrials)(batchedMediaAssets[currPreloadBatch]).default);
        currPreloadBatch++;
    }
    function getPracticeTransitionPrompt() {
        return heavyInstructions && (0, taskStore_1.taskStore)().nextStimulus.trialType === '2D'
            ? 'mentalRotationInstruct5Downex'
            : 'generalYourTurn';
    }
    const numOfTrials = corpus.length;
    (0, taskStore_1.taskStore)('totalTestTrials', (0, helpers_1.getRealTrials)(corpus));
    const numOfInitialPracticeTrials = firstBlockPractice.length;
    const fallbackIndex = numOfInitialPracticeTrials + 4;
    for (let i = 0; i < numOfTrials; i++) {
        if (i % batchSize === 0) {
            preloadBatch();
        }
        if (i <= fallbackIndex) {
            timeline.push(fallbackInstructions);
        }
        timeline.push({ ...trials_1.setupStimulus, stimulus: '' });
        timeline.push((0, trials_1.practiceTransition)(getPracticeTransitionPrompt));
        timeline.push(threeDimInstructBlock);
        timeline.push(polygonInstructBlock);
        timeline.push(stimulusBlock);
    }
    (0, taskSetup_1.initializeCat)();
    timeline.push((0, trials_1.taskFinished)());
    timeline.push(trials_1.exitFullscreen);
    return { jsPsych: taskSetup_1.jsPsych, timeline };
}
